import React from 'react';
import { useTranslation } from 'react-i18next';


const variantClasses = {
  primary:
    'text-white bg-buttonBg shadow-sm hover:shadow-md hover:brightness-110 active:scale-[0.97] active:shadow-sm border border-buttonBg',
  orange:
    'text-white bg-orange-500 shadow-sm hover:shadow-md hover:brightness-110 active:scale-[0.97] active:shadow-sm border border-orange-500 hover:border-orange-600',
  secondary:
    'text-buttonBg bg-white shadow-sm hover:bg-gray-50 hover:shadow-md active:scale-[0.97] border border-gray-300',
  danger:
    'text-white bg-red-500 shadow-sm hover:bg-red-600 hover:shadow-md active:scale-[0.97] border border-red-500 hover:border-red-600',
  success:
    'text-white bg-emerald-500 shadow-sm hover:bg-emerald-600 hover:shadow-md active:scale-[0.97] border border-emerald-500 hover:border-emerald-600',
  reset:
    'text-white bg-resetbuttonbg shadow-sm hover:bg-emerald-600 hover:shadow-md active:scale-[0.97] border border-resetbuttonbg hover:border-emerald-600',
  pagination:
    'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300 border border-gray-200',
  ghost:
    'text-gray-600 hover:bg-gray-100 active:bg-gray-200 border border-transparent',
  link:
    'text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-none p-0',
};

const sizeClasses = {
  sm: 'px-2.5 py-1.5 text-xs',
  md: 'px-4 py-2 text-xs sm:text-sm',
  lg: 'px-5 py-2.5 text-sm sm:text-base',
};

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  icon: Icon,
  iconPosition = 'left',
  loading = false,
  disabled = false,
  fullWidth = false,
  mobileIconOnly = false,
  mobileLabel,
  className = '',
  type = 'button',
  ...rest
}) => {
  const isDisabled = disabled || loading;

  const base =
    'group inline-flex items-center gap-2 rounded-lg transition-all duration-200 ease-out font-medium whitespace-nowrap select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-buttonBg/50 focus-visible:ring-offset-1';

  const hasJustify = /justify-/.test(className);

  const resolvedMobileLabel =
    mobileLabel ||
    rest.title ||
    rest['aria-label'] ||
    (typeof children === 'string' ? children : undefined);

  const mobileCompactClass = mobileIconOnly ? 'max-md:px-2.5 max-md:py-2 max-md:gap-0 max-md:w-auto' : '';

  const classes = [
    base,
    !hasJustify ? 'justify-center' : '',
    variantClasses[variant] || variantClasses.primary,
    variant !== 'link' ? sizeClasses[size] || sizeClasses.md : sizeClasses[size] || '',
    fullWidth && !mobileIconOnly ? 'w-full' : '',
    fullWidth && mobileIconOnly ? 'w-full max-md:w-auto' : '',
    isDisabled ? 'opacity-50 cursor-not-allowed pointer-events-none' : 'cursor-pointer',
    mobileCompactClass,
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const iconSize = size === 'sm' ? 'w-3.5 h-3.5' : 'w-4 h-4';

  const renderedChildren =
    mobileIconOnly && children != null && children !== false
      ? typeof children === 'string' || typeof children === 'number'
        ? (
            <span className="hidden md:inline">{children}</span>
          )
        : (
            <span className="hidden md:contents">{children}</span>
          )
      : children;

  const a11yProps =
    mobileIconOnly && resolvedMobileLabel
      ? { title: resolvedMobileLabel, 'aria-label': resolvedMobileLabel }
      : {};

  return (
    <button type={type} className={classes} disabled={isDisabled} {...a11yProps} {...rest}>
      {loading && (
        <svg
          className={`animate-spin ${iconSize}`}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
        >
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
        </svg>
      )}
      {!loading && Icon && iconPosition === 'left' && (
        <Icon className={`${iconSize} transition-transform duration-200 group-hover:scale-110`} />
      )}
      {renderedChildren}
      {!loading && Icon && iconPosition === 'right' && (
        <Icon className={`${iconSize} transition-transform duration-200 group-hover:scale-110`} />
      )}
    </button>
  );
};

export default Button;
